// NACX Metrics — Samsung overlay analytics event lifecycle.
// Uses @samsung360vr/overlay-analytics which handles SDK initialization,
// event queuing, privacy (VIS consent / TIFA redaction), and retries internally.

import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { AnalyticsService } from '@samsung360vr/overlay-analytics';
import type { OverlayEvent, OverlayInteraction } from '@samsung360vr/overlay-analytics';
import { logInfo, logWarn } from '@/utils/logger';

// // Constants

const appInstance = import.meta.env.VITE_APP_ENV;
const isTestMode = import.meta.env.VITE_APP_NACX_TEST_MODE === 'true';

export const OVERLAY_ERROR = {
  DATA_LOAD_FAILED: 'DATA_LOAD_FAILED',
  IS_VISIBLE_REJECTED: 'IS_VISIBLE_REJECTED',
  SLOT_DURATION_BELOW_THRESHOLD: 'SLOT_DURATION_BELOW_THRESHOLD',
  POSITION_UPDATES_STALLED: 'POSITION_UPDATES_STALLED',
  FADE_OUT_SCREEN_NOT_REACHED: 'FADE_OUT_SCREEN_NOT_REACHED',
  // Poll's 1s post-vote graceful-update GET was stale; a retry was scheduled.
  POLL_RESULTS_STALE_AFTER_VOTE: 'POLL_RESULTS_STALE_AFTER_VOTE',
  // The retry GET was also stale — backend still hadn't caught up.
  POLL_RESULTS_STALE_AFTER_RETRY: 'POLL_RESULTS_STALE_AFTER_RETRY',
} as const;

// // Private state

let analytics: AnalyticsService | null = null;

/**
 * Check if event sending is enabled in the current environment.
 * Events fire only in OEF content overlay environments (detected via SDK)
 * in dev/stage/prod, OR when test mode is enabled via env var.
 */
const isSendingEnabled = (): boolean => {
  if (isTestMode) return true;
  return CommunicationUtil.isContentOverlay() && ['dev', 'stage', 'prod'].includes(appInstance);
};

// // Public API

/**
 * Initialize metrics for this overlay session.
 * Creates the AnalyticsService which automatically fetches SDK context,
 * handles VIS consent, and queues events until ready.
 */
export const initializeMetrics = (): void => {
  logInfo('[nacxMetrics] initializeMetrics', { isTestMode });

  if (!isSendingEnabled()) {
    logInfo('[nacxMetrics] Skipped — sending not enabled');
    return;
  }

  logInfo('[nacxMetrics] Creating AnalyticsService');
  analytics = new AnalyticsService({
    appVersion: __APP_VERSION__,
  });
};

/**
 * Send a metrics event. Fire-and-forget.
 * Silently skips if metrics are not initialized (sending disabled or never called).
 */
const sendEvent = (event: OverlayEvent): void => {
  if (!analytics) {
    logInfo(`[nacxMetrics] sendEvent skipped (no analytics instance): ${event.name}`);
    return;
  }

  logInfo(`[nacxMetrics] sendEvent: ${event.name}`);
  analytics
    .track(event)
    .catch((err) => logWarn(`[nacxMetrics] Event delivery failed: ${event.name}`, err));
};

// // Convenience actions for each event type

export const sendOverlayStarted = (): void => {
  sendEvent({
    name: 'nacxOverlayStarted',
  });
};

export const sendOverlayShown = (): void => {
  sendEvent({ name: 'nacxOverlayShown' });
};

export const sendOverlayInteraction = (interaction: OverlayInteraction): void => {
  sendEvent({ name: 'nacxOverlayInteraction', data: { interaction } });
};

export const sendOverlayCompleted = (): void => {
  sendEvent({
    name: 'nacxOverlayCompleted',
  });
};

/** Fires both interaction + completed events. Use when selecting an option IS the completion. */
export const sendInteractionCompleted = (interaction: OverlayInteraction): void => {
  sendOverlayInteraction(interaction);
  sendOverlayCompleted();
};

export const sendOverlayDismissed = (): void => {
  sendEvent({
    name: 'nacxOverlayDismissed',
  });
};

export const sendOverlayError = (code: string, message: string): void => {
  sendEvent({ name: 'nacxOverlayError', data: { error: { code, message } } });
};
